import React from "react";
import SiteShell from "@/components/SiteShell";
import LeadForm from "@/components/LeadForm";
import WineryListingTiers from "@/components/WineryListingTiers";
import VinesAppPreview from "@/components/VinesAppPreview";
import {
  customMapVerticals,
  buildSteps,
  platformPersonas,
  platformDeliverables,
} from "@/data/platforms";
import {
  ArrowRight,
  ArrowUpRight,
  MapPin,
  Smartphone,
  Globe,
  Database,
  Bell,
  TrendingUp,
  Layers,
  Beer,
  Wine,
  BedDouble,
  Tent,
  Utensils,
  Landmark,
  Leaf,
  Sparkles,
  Heart,
  Palette,
  Wheat,
} from "lucide-react";

const ICONS = {
  Beer, Wine, BedDouble, Tent, Utensils, Landmark,
  Leaf, Sparkles, Heart, Palette, Wheat, MapPin,
  Smartphone, Globe, Database, Bell, TrendingUp, Layers,
};

const Platforms = () => {
  return (
    <SiteShell>
      {/* HERO — benefit-led, audience-centric */}
      <section
        className="relative bg-[var(--af-navy)] text-[var(--af-cream)] grid-bg overflow-hidden"
        data-testid="platforms-hero"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 pb-12 sm:pt-16 sm:pb-14 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="eyebrow mb-6">
                Map + Mobile App Platforms · Built, Owned, Operated
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mb-7">
                Get on the map.
                <br />
                <span className="text-[var(--af-lime)]">Literally.</span>
              </h1>
              <p className="text-lg sm:text-xl text-[var(--af-cream)]/75 max-w-2xl mb-8 leading-relaxed">
                An interactive map + mobile app your customers actually open
                when they&apos;re planning a trip. Built for wineries, DMOs,
                hotels, hosts, and any niche you want to own.{" "}
                <strong>vines.app</strong> is already live for Virginia wine —
                yours can be next.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://vines.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-lime"
                  data-testid="platforms-hero-cta-vines"
                >
                  See vines.app live <ArrowUpRight size={14} />
                </a>
                <a
                  href="#personas"
                  className="btn-ghost"
                  data-testid="platforms-hero-cta-build"
                >
                  Who it&apos;s for
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="surface p-6 fade-up" style={{ animationDelay: ".15s" }}>
                <div className="eyebrow mb-4">Four ways to own your map</div>
                <ul className="space-y-3.5">
                  {[
                    {
                      icon: Wine,
                      label: "Wineries & Listable Businesses",
                      d: "Be findable when trip-planning happens.",
                    },
                    {
                      icon: Landmark,
                      label: "DMOs & Tourism Boards",
                      d: "Fix your thin-content SEO problem.",
                    },
                    {
                      icon: BedDouble,
                      label: "Hotels & Hosts",
                      d: "A concierge in every guest's pocket.",
                    },
                    {
                      icon: Layers,
                      label: "Brands & Associations",
                      d: "Customize the platform for your niche.",
                    },
                  ].map((p, i) => {
                    const Icon = p.icon;
                    return (
                      <li
                        key={i}
                        className="flex items-start gap-3"
                        data-testid={`hero-persona-summary-${i}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon size={14} className="text-[var(--af-lime)]" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-display text-base leading-tight">
                            {p.label}
                          </div>
                          <div className="text-xs text-[var(--af-cream)]/70 mt-0.5 leading-snug">
                            {p.d}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-5 pt-4 border-t border-[var(--af-line)]">
                  <a
                    href="#personas"
                    className="text-xs font-semibold uppercase tracking-widest text-[var(--af-lime)] hover:underline underline-offset-4 inline-flex items-center gap-1"
                    data-testid="hero-personas-cta"
                  >
                    See the full breakdown <ArrowRight size={12} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTERACTIVE PREVIEW: live map + mobile app video — top-fold */}
      <div className="border-y border-[var(--af-line)] bg-[var(--af-navy-deep)]">
        <VinesAppPreview
          hideIntro
          className="px-5 sm:px-8 pt-10 pb-16 max-w-7xl mx-auto"
        />
      </div>

      {/* WHO IT'S FOR — Persona cards (the core selling section) */}
      <section
        id="personas"
        className="px-5 sm:px-8 py-24 max-w-7xl mx-auto"
        data-testid="personas-section"
      >
        <div className="mb-14 max-w-3xl">
          <div className="eyebrow mb-4">Who wins on the platform</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4 leading-tight">
            Four ways to{" "}
            <span className="text-[var(--af-lime)]">own your map.</span>
          </h2>
          <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-2xl leading-relaxed">
            The same platform engine powers very different outcomes depending
            on who&apos;s plugging in. Find the one that fits.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-5 lg:gap-7">
          {platformPersonas.map((p) => {
            const Icon = ICONS[p.icon] || MapPin;
            return (
              <div
                key={p.key}
                className="surface p-7 lg:p-9 flex flex-col"
                data-testid={`persona-${p.key}`}
              >
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 rounded-full bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 flex items-center justify-center flex-shrink-0">
                    <Icon size={22} className="text-[var(--af-lime)]" />
                  </div>
                  <div className="eyebrow">{p.eyebrow}</div>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl mb-4 leading-tight">
                  {p.headline}
                </h3>
                <p className="text-sm sm:text-base text-[var(--af-cream)]/75 leading-relaxed mb-7 flex-1">
                  {p.body}
                </p>
                <a
                  href={p.cta.anchor}
                  className="text-sm font-semibold text-[var(--af-lime)] hover:underline underline-offset-4 inline-flex items-center gap-2"
                  data-testid={`persona-${p.key}-cta`}
                >
                  {p.cta.label} <ArrowRight size={14} />
                </a>
              </div>
            );
          })}
        </div>
      </section>

      {/* TWO PATHS STRIP */}
      <section
        className="px-5 sm:px-8 pb-24 max-w-7xl mx-auto"
        data-testid="two-paths-section"
      >
        <div className="grid md:grid-cols-2 gap-5">
          <a
            href="#winery-tiers"
            className="surface p-7 lg:p-8 flex items-start gap-5 group hover:border-[var(--af-lime)]/60 transition-colors"
            data-testid="path-winery"
          >
            <div className="w-12 h-12 rounded-full bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 flex items-center justify-center flex-shrink-0">
              <Wine size={22} className="text-[var(--af-lime)]" />
            </div>
            <div className="flex-1">
              <div className="eyebrow mb-2">Already a VA winery?</div>
              <div className="font-display text-xl mb-2">
                Get listed on vines.app
              </div>
              <div className="text-sm text-[var(--af-cream)]/70 leading-relaxed">
                Free standard listing. Upgrade for featured placement, push
                notifications, and a monthly performance report.
              </div>
            </div>
            <ArrowRight
              size={20}
              className="text-[var(--af-cream)]/40 group-hover:text-[var(--af-lime)] group-hover:translate-x-1 transition-all flex-shrink-0 mt-1"
            />
          </a>

          <a
            href="#lead-form"
            className="surface p-7 lg:p-8 flex items-start gap-5 group hover:border-[var(--af-lime)]/60 transition-colors"
            data-testid="path-build"
          >
            <div className="w-12 h-12 rounded-full bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 flex items-center justify-center flex-shrink-0">
              <Layers size={22} className="text-[var(--af-lime)]" />
            </div>
            <div className="flex-1">
              <div className="eyebrow mb-2">Building for your category?</div>
              <div className="font-display text-xl mb-2">
                Get your own branded platform
              </div>
              <div className="text-sm text-[var(--af-cream)]/70 leading-relaxed">
                Customized for your industry. Ready to launch in 6–10 weeks
                with a full paid-media kickoff.
              </div>
            </div>
            <ArrowRight
              size={20}
              className="text-[var(--af-cream)]/40 group-hover:text-[var(--af-lime)] group-hover:translate-x-1 transition-all flex-shrink-0 mt-1"
            />
          </a>
        </div>
      </section>

      {/* WINERY LISTING TIERS */}
      <div className="border-y border-[var(--af-line)]">
        <WineryListingTiers />
      </div>

      {/* WHAT YOU GET — concrete deliverables */}
      <section
        className="px-5 sm:px-8 py-24 max-w-7xl mx-auto"
        data-testid="deliverables-section"
      >
        <div className="mb-12 max-w-3xl">
          <div className="eyebrow mb-4">What ships with every build</div>
          <h2 className="font-display text-4xl sm:text-5xl mb-4 leading-tight">
            The whole engine,{" "}
            <span className="text-[var(--af-lime)]">your brand.</span>
          </h2>
          <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-2xl leading-relaxed">
            No piecemeal scoping. Every custom build includes the full stack
            we run on vines.app — customized for your category and live in
            6–10 weeks.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {platformDeliverables.map((d, i) => {
            const Icon = ICONS[d.icon] || MapPin;
            return (
              <div
                key={i}
                className="surface p-6"
                data-testid={`deliverable-${i}`}
              >
                <Icon size={22} className="text-[var(--af-lime)] mb-3" />
                <div className="font-display text-lg mb-2 leading-tight">
                  {d.t}
                </div>
                <div className="text-sm text-[var(--af-cream)]/70 leading-relaxed">
                  {d.d}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* VERTICALS WE CAN MAP */}
      <section
        className="px-5 sm:px-8 pb-24 max-w-7xl mx-auto"
        data-testid="custom-maps-section"
      >
        <div className="mb-10 max-w-3xl">
          <div className="eyebrow mb-4">Customizable for your industry</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-4 leading-tight">
            If it has locations,{" "}
            <span className="text-[var(--af-lime)]">we can map it.</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {customMapVerticals.map((v, i) => {
            const Icon = ICONS[v.icon] || MapPin;
            return (
              <div
                key={i}
                className="surface p-5 flex items-center gap-4"
                data-testid={`vertical-${i}`}
              >
                <div className="w-10 h-10 rounded-full bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className="text-[var(--af-lime)]" />
                </div>
                <div className="font-display text-base leading-tight">
                  {v.name}
                </div>
              </div>
            );
          })}
        </div>

        <div className="surface p-7 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="eyebrow mb-2">Turnkey builds</div>
            <div className="font-display text-2xl sm:text-3xl mb-2 leading-tight">
              Your platform,{" "}
              <span className="text-[var(--af-lime)]">launched in 6–10 weeks.</span>
            </div>
            <p className="text-sm text-[var(--af-cream)]/75 max-w-xl">
              Includes discovery, build, launch campaign, admin tooling, and
              data ingestion. Custom pricing based on your category and scope.
            </p>
          </div>
          <a
            href="#lead-form"
            className="btn-lime"
            data-testid="custom-maps-cta"
          >
            Discuss a build <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        className="border-y border-[var(--af-line)] bg-[var(--af-navy-deep)] px-5 sm:px-8 py-24"
        data-testid="how-it-works-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 max-w-3xl">
            <div className="eyebrow mb-4">How it works</div>
            <h2 className="font-display text-4xl sm:text-5xl mb-4 leading-tight">
              Three phases.{" "}
              <span className="text-[var(--af-lime)]">One platform.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 lg:gap-7">
            {buildSteps.map((s, i) => (
              <div
                key={s.n}
                className="surface p-7"
                data-testid={`step-${i}`}
              >
                <div className="font-display text-5xl text-[var(--af-lime)] mb-3 leading-none">
                  {s.n}
                </div>
                <div className="font-display text-xl mb-2">{s.t}</div>
                <p className="text-sm text-[var(--af-cream)]/75 leading-relaxed">
                  {s.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section
        id="lead-form"
        className="grid-bg px-5 sm:px-8 py-24 border-t border-[var(--af-line)]"
        data-testid="lead-form-section"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="eyebrow mb-4">Get listed, get built, get launched</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight">
              Tell us what you want
              <br />
              <span className="text-[var(--af-lime)]">to own.</span>
            </h2>
            <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-md mb-7 leading-relaxed">
              Whether you&apos;re a winery wanting featured placement, a DMO ready
              to fix your SEO problem, a host who wants a guest-experience
              app, or a brand ready to own a category — start here.
            </p>
            <ul className="space-y-2.5 text-sm text-[var(--af-cream)]/75">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Free 20-minute platform consultation
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Honest fit + scope conversation
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Real timelines, real pricing, no surprises
              </li>
            </ul>
          </div>
          <LeadForm
            trade="platforms-inquiry"
            tradeName="Platforms"
            headline="Tell us about your platform goal."
          />
        </div>
      </section>
    </SiteShell>
  );
};

export default Platforms;
